import { isRegisteredFeature, isRegisteredModule, PLATFORM_CORE_MODULE_KEY, } from '@tessma/registry';
import { isRegisteredPermission, permissionDefinition } from './permissions.js';
const ALLOWED = Object.freeze({ allowed: true });
/**
 * Platform Core is mandatory for every tenant and is never subject to
 * entitlement (COMMERCIAL_MODULARITY §1). Its module key is therefore exempt
 * from the entitlement check rather than granted by it. Re-exported from the
 * registry, which owns it — every enforcement point resolves through one list
 * rather than restating the string (§3).
 */
export { PLATFORM_CORE_MODULE_KEY };
export function authorise(context, requirement, options = {}) {
    // ---- 1. entitlement, first, because it is cheapest and most decisive -----
    // Every enforcement point resolves through the registry; nothing hardcodes a
    // module name in a conditional (COMMERCIAL_MODULARITY §3). A module key the
    // registry does not know is a programming error and fails closed — as
    // not-found, because even the shape of the answer must not confirm that
    // something exists there.
    const moduleKey = requirement.moduleKey ?? requirement.permission.split('.')[0];
    if (moduleKey === undefined || !isRegisteredModule(moduleKey)) {
        return { allowed: false, reason: 'unregistered_module', response: 'not_found' };
    }
    if (requirement.featureKey !== undefined && !isRegisteredFeature(requirement.featureKey)) {
        // Default deny on an unknown feature key (§3.2).
        return { allowed: false, reason: 'unregistered_feature', response: 'not_found' };
    }
    if (moduleKey !== PLATFORM_CORE_MODULE_KEY) {
        if (!context.entitlements.modules.has(moduleKey)) {
            return { allowed: false, reason: 'not_entitled', response: 'not_found' };
        }
        if (requirement.featureKey !== undefined &&
            !context.entitlements.features.has(requirement.featureKey)) {
            return { allowed: false, reason: 'feature_not_entitled', response: 'not_found' };
        }
    }
    // ---- 2. permission -------------------------------------------------------
    // An unregistered permission is a programming error, and it fails CLOSED: a
    // typo in a permission string must not become an open door. It is checked
    // HERE rather than before entitlement, because the approved chain puts
    // entitlement at step 2 and permission at step 3 (§6.1) — and the order is
    // load-bearing. An endpoint of a module the tenant has not bought must answer
    // not found whether or not its permission happens to be registered yet;
    // answering `forbidden` for an unregistered one would tell an unentitled
    // caller which modules exist, which is the disclosure the ordering prevents.
    if (!isRegisteredPermission(requirement.permission)) {
        return { allowed: false, reason: 'unregistered_permission', response: 'forbidden' };
    }
    if (!context.permissions.values.has(requirement.permission)) {
        return { allowed: false, reason: 'no_permission', response: 'forbidden' };
    }
    // ---- 3. scope ------------------------------------------------------------
    if (requirement.scopeNodeId !== undefined) {
        const covered = options.coveredNodes;
        if (covered === undefined || !covered.has(requirement.scopeNodeId)) {
            // Out of scope is answered as not found, not as forbidden: a 403 on a
            // node the actor may not see confirms that the node exists.
            return { allowed: false, reason: 'out_of_scope', response: 'not_found' };
        }
    }
    // ---- 4. step-up ----------------------------------------------------------
    const definition = permissionDefinition(requirement.permission);
    if (definition?.stepUpRequired === true && options.stepUpSatisfied !== true) {
        return { allowed: false, reason: 'step_up_required', response: 'step_up' };
    }
    return ALLOWED;
}
/**
 * The single-record form, for UI affordances and for checks that already hold
 * the record. Record-level rules are applied by the predicate layer; this
 * answers the entity-level question only.
 */
export function can(context, requirement, options = {}) {
    return authorise(context, requirement, options).allowed;
}
export class AuthorisationError extends Error {
    decision;
    constructor(decision) {
        // Deliberately uniform. The decision carries what the caller should be
        // told; the message never does.
        super('Not permitted');
        this.name = 'AuthorisationError';
        this.decision = decision;
    }
}
export function assertAuthorised(context, requirement, options = {}) {
    const decision = authorise(context, requirement, options);
    if (!decision.allowed)
        throw new AuthorisationError(decision);
}
//# sourceMappingURL=authorise.js.map